import { EmployeeForShiftItem } from '@/types/employeeForShift.types';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown } from 'react-icons/fi';
import { merge } from '../../../helpers/Utility';
import strings from '../roster.translations';
import { dropdownId, getDropdownSettings, shiftsWrapperId } from './rosterPickerHelper';
import { RosterPickerOption } from './RosterPickerOption';
import { WarningsIndicator } from './warningsIndicator/WarningsIndicator';

interface RosterPickerPanelProps {
  options: EmployeeForShiftItem[];
  onChange: (newVal?: EmployeeForShiftItem) => void;
  value?: EmployeeForShiftItem;
  close: () => void;
}

const RosterPickerPanel: FC<RosterPickerPanelProps> = ({ options, value, onChange, close }) => {
  useEffect(() => {
    const wrapper = document.querySelector(`#${shiftsWrapperId}`);
    wrapper?.addEventListener('scroll', close);

    return () => wrapper?.removeEventListener('scroll', close);
  }, [close]);

  return (
    <>
      <div className={'border-b p-2 font-semibold'}>{strings.staffSelector.pleaseSelect}</div>
      {options.map(opt => {
        const selected = value?.employeeId === opt.employeeId;
        return (
          <RosterPickerOption
            option={opt}
            key={opt.employeeId}
            selected={selected}
            onSelect={() => {
              onChange(selected ? undefined : opt);
              close();
            }}
          />
        );
      })}
    </>
  );
};

interface RosterPickerProps {
  shiftId: string;
  options: EmployeeForShiftItem[];
  onChange: (newVal?: EmployeeForShiftItem) => void;
  value?: EmployeeForShiftItem;
}

export const RosterPicker: FC<RosterPickerProps> = ({ shiftId, options, onChange, value }) => {
  const staffAvailable = options.length > 0;

  return (
    <Popover className="flex-1">
      {({ open }) => (
        <>
          <PopoverButton
            className={merge(
              'w-full flex items-center py-1 pl-3 relative cursor-default overflow-hidden rounded-lg border gap-2',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75'
            )}
            id={dropdownId(shiftId)}
            disabled={!staffAvailable}
          >
            <WarningsIndicator shiftId={shiftId} selectedUser={value} />
            <div className="flex-1 w-full border-none text-sm leading-5 focus:ring-0 text-ellipsis text-left">
              {!staffAvailable ? strings.noStaff : !value ? strings.notAssigned : value.name}
            </div>
            {staffAvailable && <FiChevronDown className="h-9  w-9" />}
          </PopoverButton>
          {/*
           * Slightly weird bit, for a few reasons.
           *
           * We need to use 'createPortal' here because there's a complicated situation with Z-indexes. I've
           * forgetten what the exact issue is, but basically, there's a situation where you need this component to
           * be behind something, but create a modal which appears above it's own parent. This is impossible with normal
           * z-indexes, so we use a portal- it basically causes the panel to be managed here, but in the DOM, it appears at
           * the top level, so can have whatever z-index it needs.
           *
           * The other weird thing is that we're using the open attribute to manually control this panel being rendered.
           * Normally,
           */}
          {!open ? (
            <></>
          ) : (
            createPortal(
              <PopoverPanel
                style={getDropdownSettings(shiftId, options)}
                className="flex flex-col absolute z-[1000] bg-white border overflow-auto w-[400px] rounded-md"
              >
                {({ close }) => <RosterPickerPanel close={close} value={value} options={options} onChange={onChange} />}
              </PopoverPanel>,
              document.body
            )
          )}
        </>
      )}
    </Popover>
  );
};
